import React from 'react'
import AuthPageLayout from '../../layout/auth_page_layout';
import TextField from '../../components/textfield';
import Button from '../../components/button';
import { useOnInputChange } from '../../hooks/useOnInputChange';
import { useAsyncOperation } from '../../hooks/useAsyncOperation';
import { AuthService } from '../../apilibrary';
import Typography, { TypographyColor } from '../../components/typography';
import { useNavigate } from 'react-router-dom';

interface SendRecoveryCodeStepProps {
  setRecoverEmail: (email: string) => void
}

const SendRecoveryCodeStep: React.FC<SendRecoveryCodeStepProps> = ({
  setRecoverEmail: recoverEmail
}) => {
  const [email, onChangeEmail] = useOnInputChange('')
  const {
    runFunction: sendForgetPasswordCode,
    loading: loadingSendForgetPasswordCode,
    errorMessage: errorMessageSendForgetPasswordCode
  } = useAsyncOperation(AuthService.sendForgetPasswordCode)

  const handleClickSendRecoveryCode = async (): Promise<void> => {
    try {
      await sendForgetPasswordCode(email)
      recoverEmail(email)
    } catch (e) {
      console.log(e)
    }
  }

  return (
    <>
      <TextField
        value={email}
        onChange={onChangeEmail}
        fullWidth
        gutterBottom
        label='Email'
      />
      {errorMessageSendForgetPasswordCode &&
        <Typography color={'error'}>{errorMessageSendForgetPasswordCode}</Typography>
      }
      <Button
        fullWidth
        disabled={loadingSendForgetPasswordCode}
        onClick={handleClickSendRecoveryCode}
        variant='contained'
        color='primary'
        sx={{ marginTop: '12px' }}
      >
        Send Recovery Code
      </Button>
    </>
  )
}

export interface ResetPasswordStepProps {
  recoveryEmail: string
}

const ResetPasswordStep: React.FC<ResetPasswordStepProps> = ({
  recoveryEmail
}) => {
  const [password, onChangePassword] = useOnInputChange('')
  const [confirmPassword, onChangeConfirmPassword] = useOnInputChange('')
  const [code, onChangeCode] = useOnInputChange('')
  const navigate = useNavigate()
  const {
    runFunction: resetPassword,
    loading: loadingResetPassword
  } = useAsyncOperation(AuthService.resetPassword)
  const [errorMessage, setErrorMessage] = React.useState<string>()

  const handleClickResetPassword = async (): Promise<void> => {
    try {
      if (password !== confirmPassword) {
        throw new Error('Password and Confirm Password must be the same')
      }
      await resetPassword({
        code,
        email: recoveryEmail,
        newPassword: password
      })
      navigate('/auth/login?email=' + recoveryEmail)
    } catch (e) {
      if (e instanceof Error) {
        setErrorMessage(e.message)
      } else {
        setErrorMessage('Something went wrong')
      }
    }
  }

  return (
    <>
      <Typography color={TypographyColor.SECONDARY} variant='subtitle2'>
        Please check your email <b>{recoveryEmail}</b> for the recovery code.
      </Typography>
      <TextField
        fullWidth
        value={code}
        onChange={onChangeCode}
        gutterBottom
        label='Recovery Code'
      />
      <TextField
        value={password}
        onChange={onChangePassword}
        fullWidth
        gutterBottom
        label='New Password'
        type='password'
      />
      <TextField
        value={confirmPassword}
        onChange={onChangeConfirmPassword}
        fullWidth
        gutterBottom
        label='Confirm Password'
        type='password'
      />
      {errorMessage &&
        <Typography color={'error'}>{errorMessage}</Typography>
      }
      <Button
        fullWidth
        onClick={handleClickResetPassword}
        variant='contained'
        disabled={loadingResetPassword}
        color='primary'
        sx={{ marginTop: '12px' }}
      >
        Reset Password
      </Button>
    </>
  )
}

const ForgetPasswordPage: React.FC = () => {
  const [recoverEmail, setRecoverEmail] = React.useState<string>()

  return (
    <AuthPageLayout title='Forget Password'>
      {!recoverEmail
        ? <SendRecoveryCodeStep setRecoverEmail={setRecoverEmail} />
        : <ResetPasswordStep recoveryEmail={recoverEmail} />
      }
    </AuthPageLayout>
  )
}

export default ForgetPasswordPage;
